import React, { PropsWithChildren } from 'react';
import { useLocation, Navigate, Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { IconSearch, IconPlus } from '@tabler/icons';

import Logo from './Logo';
import Footer from './Footer';
import { useToggle } from 'src/hooks';

import { useUser } from 'src/queries/user';
import SomethingAdd from 'src/Components/Something/SomethingAdd';
import SearchBar from 'src/Components/Search/SearchBar';
import { Separator } from 'src/UIComponents';

import {
  Layout,
  StyledNavigation,
  GradientBackground,
  Content,
  FullScreen,
  UserAvatar,
  ProfileLink,
  ExpandBar,
  Bar,
  Actions,
  SvgButton,
} from './Private.styles';

type PrivateLayoutProps = PropsWithChildren<{
  fullscreen?: boolean;
}>;

function PrivateLayout({ children, fullscreen }: PrivateLayoutProps) {
  const location = useLocation();
  const [showSearch, toggleSearchOpen] = useToggle();
  const [showAddSomething, toggleAddSomething] = useToggle();
  const { isLoading, data: user } = useUser('me');

  if (!isLoading && !user) {
    // Trying to access private route without being authenticated
    const redirectUrl = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} />
    );
  }

  return (
    <Layout>
      <GradientBackground>
        <div className="container">
          <Logo />

          <Bar>
            <StyledNavigation />

            <Actions>
              <SvgButton title="Search" onClick={toggleSearchOpen}>
                <IconSearch />
              </SvgButton>

              <SvgButton title="Add Something" onClick={toggleAddSomething}>
                <IconPlus />
              </SvgButton>
            </Actions>

            <ProfileLink to="/user/profile">
              {user?.avatarUrl && (
                <UserAvatar
                  alt="Profile"
                  src={user.avatarUrl}
                  title={isLoading ? '...' : `Your Profile, ${user.firstName}`}
                />
              )}
            </ProfileLink>
          </Bar>

          {showSearch && (
            <ExpandBar>
              <SearchBar onClose={toggleSearchOpen} />
            </ExpandBar>
          )}
          {showAddSomething && (
            <ExpandBar>
              <SomethingAdd
                onClose={toggleAddSomething}
                placeholder="Type Something"
              />
            </ExpandBar>
          )}
        </div>
      </GradientBackground>

      <Content>
        {fullscreen ? (
          <FullScreen>{children ? children : <Outlet />}</FullScreen>
        ) : (
          <div
            className="container no-fullscreen"
            style={{
              paddingTop: '1em',
            }}
          >
            {children ? children : <Outlet />}

            <Separator style={{ marginBottom: 0 }} />
            <Footer />
          </div>
        )}
      </Content>
    </Layout>
  );
}

PrivateLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  redirectPath: PropTypes.string,
};

export default PrivateLayout;
